/* eslint-disable no-unused-vars */
/* eslint-disable no-console */
import React, { Ref, useEffect, useMemo, useState } from 'react';
import { Col, Card, Input, Flex, Button, theme, Row, Modal } from 'antd';
import {
  CheckCircleFilled,
  CloseCircleFilled,
  DeleteOutlined,
  DropboxOutlined,
  LoadingOutlined,
  QuestionCircleFilled,
  RightOutlined,
  ScanOutlined,
  WarningFilled,
} from '@ant-design/icons';
import useCheckDesktop from 'hooks/useIsDesktop';
import { useDebounce } from 'util/Hooks';
import QRScanner from 'components/QRScanner';
import { useStylesGenerateRoute } from '../styles';
import { ISSUE_CODES, useAddShipment } from 'services/journeys';
import { useJourneyDashboardContext } from '../context';
import { useGetByExternalCode } from 'services/PackageService';
import { Shipment } from 'interfaces/journey';
import { useSelector } from 'react-redux';
import { selectGlobals } from 'redux/globals/selectors';
import { GlobalEnum } from 'interfaces/globals';
import { Marketplace, useMarketplaces } from 'services/MarketplaceService';
import { CoreShipment } from 'interfaces/packet';

const WAIT_INPUT_EXTERNAL_CODE = 1500;

interface PackageInputProps {
  disabled?: boolean;
}

const PackageInput = React.forwardRef<HTMLDivElement, PackageInputProps>(
  ({ disabled }, ref) => {
    const globals = useSelector(selectGlobals);
    const { token } = theme.useToken();
    const isDesktop = useCheckDesktop();
    const classes = useStylesGenerateRoute({ theme: token });
    const { id, message, inputResponse, inputtedExternal, inputCode } =
      useJourneyDashboardContext();
    const { data: marketplaces } = useMarketplaces();
    const [open, setOpen] = useState<boolean>(false);
    const [externalCode, setExternalCode] = useState<string>('');
    const [showScanner, setShowScanner] = useState(false);
    const [repeatedExternalPackages, setRepeatedExternalPackages] = useState<
      Shipment[] | undefined
    >();
    const { mutateAsync: addPacket, isPending } = useAddShipment();
    const debouncedExternalCode = useDebounce(
      externalCode,
      WAIT_INPUT_EXTERNAL_CODE,
    );
    const {
      data: packages,
      isLoading,
      isFetching,
    } = useGetByExternalCode(debouncedExternalCode, id);

    const loading = useMemo(
      () => isLoading || isPending || isFetching,
      [isLoading, isPending, isFetching],
    );

    const handleAddPacket = (journeyId: string, shipmentId: number) => {
      const input = document.getElementById('externalCodeInput');
      addPacket({
        journeyId,
        shipmentId,
        externalCode: debouncedExternalCode,
      });
      if (input) {
        input.focus();
      }
      setExternalCode('');
    };

    useEffect(() => {
      if (!id || !packages) return;
      if (packages.length === 1 && packages[0].id) {
        handleAddPacket(id, packages[0].id);
      } else if (packages.length > 1) {
        setOpen(true);
        const suggestedPackageOptions = packages.map((packet: CoreShipment) => {
          return {
            ...packet,
            marketplace:
              marketplaces?.find(
                (mkp: Marketplace) => mkp.id === packet.ownerID,
              )?.name ?? 'Desconocido',
            destinationAddress:
              packet.shippingScheduleDestination?.shippingAddress
                ?.addressDetail ?? 'Desconocido',
            statusDescription:
              globals.packages.status.find(
                (state: GlobalEnum) => state.value === packet.status,
              )?.name ?? '',
          } as Shipment;
        });
        setRepeatedExternalPackages(suggestedPackageOptions);
      }
    }, [packages]);

    const getStatusIcon = React.useMemo(() => {
      if (loading) {
        return <LoadingOutlined style={{ fontSize: 32, color: 'lightgrey' }} />;
      }

      if (inputResponse === undefined) {
        return <DropboxOutlined style={{ color: 'grey', fontSize: 32 }} />;
      }

      if (inputResponse === 200) {
        if (inputCode && ISSUE_CODES.has(inputCode)) {
          return <WarningFilled style={{ color: '#E49273', fontSize: 32 }} />;
        } else if (message === 'delete') {
          return <DeleteOutlined style={{ color: 'red', fontSize: 32 }} />;
        } else {
          return (
            <CheckCircleFilled style={{ color: '#7cb305', fontSize: 32 }} />
          );
        }
      }

      if (inputResponse === 404) {
        return (
          <QuestionCircleFilled style={{ color: '#254370', fontSize: 32 }} />
        );
      }

      if (inputResponse === 409 || inputResponse === 500) {
        return <CloseCircleFilled style={{ color: 'red', fontSize: 32 }} />;
      }

      return <DropboxOutlined style={{ color: 'grey', fontSize: 32 }} />;
    }, [isLoading, isPending, isFetching, inputResponse, message, inputCode]);

    const handleScanQR = (data: string | null) => {
      if (data) {
        setExternalCode(data);
        setShowScanner(false);
      }
    };

    return (
      <>
        <Modal
          open={open}
          closable
          bodyStyle={{ height: 310, overflowY: 'auto' }}
          maskClosable
          onCancel={() => {
            setExternalCode('');
            setOpen(false);
          }}
          footer={null}
          title={`Seleccione el paquete correcto para ${debouncedExternalCode}`}
          centered
        >
          {repeatedExternalPackages && repeatedExternalPackages?.length > 1 && (
            <Row style={{ width: '100%' }}>
              <Col style={{ width: '100%' }}>
                {repeatedExternalPackages.map((item: CoreShipment) => (
                  <Row
                    className={isPending ? classes.disabledRow : classes.row}
                    onClick={async () => {
                      if (isPending) return;
                      if (id && item.id) handleAddPacket(id, item.id);
                      setOpen(false);
                    }}
                    key={item.id}
                  >
                    <Card
                      variant="borderless"
                      style={{ margin: '8px 0', width: '100%' }}
                    >
                      <Flex vertical>
                        <h4>{item.externalCode}</h4>
                        <p>
                          <b>Marketplace:</b> {item.marketplace}
                        </p>
                        <p>
                          <b>Destino:</b> {item.destinationAddress}
                        </p>
                        <p>
                          <b>Estado:</b> {item.statusDescription}
                        </p>
                      </Flex>
                    </Card>
                  </Row>
                ))}
              </Col>
            </Row>
          )}
        </Modal>
        <Row style={{ width: '100%' }} ref={ref as Ref<HTMLDivElement>}>
          <Col xs={24} md={11}>
            {isDesktop ? (
              <Card
                bodyStyle={{
                  padding: '10px',
                }}
              >
                <Flex gap={8} justify="space-between">
                  <ScanOutlined style={{ fontSize: 32 }} />
                  <Input
                    id="externalCodeInput"
                    placeholder="Ingresá código externo"
                    autoFocus
                    disabled={disabled}
                    className={classes.registerPacketInput}
                    value={externalCode}
                    onChange={(e) => {
                      setExternalCode(e.target.value);
                    }}
                  />
                </Flex>
              </Card>
            ) : (
              <Col span={24}>
                <QRScanner
                  showCamera={showScanner}
                  setShowCamera={setShowScanner}
                  onScan={handleScanQR}
                />
                <Button
                  type="primary"
                  style={{ minWidth: '100%', paddingBottom: '16px' }}
                  icon={<ScanOutlined />}
                  disabled={disabled}
                  onClick={() => setShowScanner(true)}
                >
                  Haz click para abrir el scanner
                </Button>
              </Col>
            )}
          </Col>
          {isDesktop && (
            <Col span={2}>
              <Flex
                style={{ minHeight: '100%' }}
                justify="center"
                align="center"
              >
                <RightOutlined style={{ fontSize: 24 }} />
              </Flex>
            </Col>
          )}
          <Col xs={24} md={11}>
            <Card bodyStyle={{ padding: '10px' }}>
              <Row align="middle">
                <Col
                  span={inputResponse !== undefined && !loading ? 4 : 24}
                  style={{
                    textAlign: 'center',
                  }}
                >
                  {getStatusIcon}
                </Col>
                {inputResponse !== undefined && !loading && (
                  <Col span={20} style={{ textAlign: 'center' }}>
                    <p
                      title={inputtedExternal}
                      style={{
                        margin: '0 8px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontSize: 'calc(12px + 0.2vw)',
                      }}
                    >
                      {inputtedExternal}
                    </p>
                  </Col>
                )}
              </Row>
            </Card>
          </Col>
        </Row>
      </>
    );
  },
);

export default PackageInput;
